import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DECK_SIZE,
  MAX_LIVES,
  MAX_PLAYERS,
  MIN_LIVES,
  MIN_PLAYERS,
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  presetForPlayers,
  type RoomConfigPatch,
  type SessionCreated,
} from '@sincronia/shared';
import { Button, Field, Panel, Stepper, Toggle } from '../components/ui.js';
import { BrandMark } from '../components/Brand.js';
import { errorKey, useT } from '../i18n/index.js';
import { emitAck, getSocket } from '../net/socket.js';
import { lastUsedName, saveSession } from '../lib/session.js';
import { useGameStore } from '../store/gameStore.js';
import { unlockAudio } from '../lib/feedback.js';

export function CreatePage(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const setSession = useGameStore((state) => state.setSession);

  const [name, setName] = useState(() => lastUsedName());
  const [capacity, setCapacity] = useState(4);
  const [custom, setCustom] = useState(false);
  const [maxLevel, setMaxLevel] = useState(() => presetForPlayers(4).maxLevel);
  const [lives, setLives] = useState(() => presetForPlayers(4).startingLives);
  const [sound, setSound] = useState(true);
  const [vibration, setVibration] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSocket();
  }, []);

  useEffect(() => {
    if (custom) return;
    const preset = presetForPlayers(capacity);
    setMaxLevel(preset.maxLevel);
    setLives(preset.startingLives);
  }, [capacity, custom]);

  const maxAllowedLevel = Math.max(1, Math.floor(DECK_SIZE / capacity));
  const trimmed = name.trim();
  const nameValid = trimmed.length >= NICKNAME_MIN_LENGTH && trimmed.length <= NICKNAME_MAX_LENGTH;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!nameValid || busy) return;

    unlockAudio();
    setBusy(true);
    setError(null);

    const config: RoomConfigPatch = custom
      ? {
          useRecommended: false,
          maxLevel: Math.min(maxLevel, maxAllowedLevel),
          startingLives: lives,
          soundEnabled: sound,
          vibrationEnabled: vibration,
        }
      : { useRecommended: true, soundEnabled: sound, vibrationEnabled: vibration };

    const response = await emitAck<SessionCreated>('room:create', {
      name: trimmed,
      capacity,
      config,
    });

    setBusy(false);
    if (!response.ok) {
      setError(t(errorKey(response.error.code)));
      return;
    }

    saveSession(response.data, trimmed);
    setSession(response.data);
    void navigate(`/sala/${response.data.code}`, { replace: true });
  }

  return (
    <main className="safe-x safe-bottom safe-top mx-auto flex w-full max-w-md flex-1 flex-col gap-6 px-5 py-8">
      <header className="flex items-center gap-3">
        <BrandMark size={40} animated={false} />
        <h1 className="text-2xl font-semibold text-night-200">{t('create.title')}</h1>
      </header>

      <form className="flex flex-col gap-5" onSubmit={(event) => void handleSubmit(event)}>
        <Panel className="flex flex-col gap-5">
          <Field
            label={t('create.nickname')}
            placeholder={t('create.nicknamePlaceholder')}
            value={name}
            autoComplete="nickname"
            maxLength={NICKNAME_MAX_LENGTH}
            onChange={(event) => setName(event.target.value)}
            data-testid="create-name"
            required
          />

          <Stepper
            label={t('create.capacity')}
            value={capacity}
            min={MIN_PLAYERS}
            max={MAX_PLAYERS}
            onChange={setCapacity}
          />
          <p className="-mt-2 text-xs text-night-400">{t('create.capacityHint')}</p>
        </Panel>

        <Panel className="flex flex-col gap-4">
          <h2 className="text-sm font-medium tracking-wide text-night-300 uppercase">
            {t('create.rules')}
          </h2>

          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('create.rules')}>
            <Button
              variant={custom ? 'secondary' : 'primary'}
              role="radio"
              aria-checked={!custom}
              onClick={() => setCustom(false)}
            >
              {t('create.rulesRecommended')}
            </Button>
            <Button
              variant={custom ? 'primary' : 'secondary'}
              role="radio"
              aria-checked={custom}
              onClick={() => setCustom(true)}
              data-testid="create-custom-rules"
            >
              {t('create.rulesCustom')}
            </Button>
          </div>

          <Stepper
            label={t('lobby.maxLevel')}
            value={maxLevel}
            min={1}
            max={maxAllowedLevel}
            onChange={setMaxLevel}
            disabled={!custom}
          />
          <Stepper
            label={t('lobby.startingLives')}
            value={lives}
            min={MIN_LIVES}
            max={MAX_LIVES}
            onChange={setLives}
            disabled={!custom}
          />

          <Toggle label={t('lobby.sound')} checked={sound} onChange={setSound} />
          <Toggle label={t('lobby.vibration')} checked={vibration} onChange={setVibration} />
        </Panel>

        {error ? (
          <p role="alert" className="rounded-2xl bg-life/10 p-3 text-sm text-life">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={busy}
            disabled={!nameValid}
            data-testid="create-submit"
          >
            {busy ? t('create.creating') : t('create.submit')}
          </Button>
          <Button variant="ghost" fullWidth onClick={() => void navigate('/')}>
            {t('common.back')}
          </Button>
        </div>
      </form>
    </main>
  );
}
